import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMount } from "react-use";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { Typography } from "@mui/material";
import { FeatureCollection } from "geojson";
import { isEqual } from "lodash";
import type mapboxgl from "mapbox-gl";
import { FeatureIdentifier } from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import defaultStyles from "fond/draw/defaultStyles";
import { baseDrawControlModes } from "fond/draw/helper";
import { GenericValidator, validateCreatedFeatures, ValidationFunction } from "fond/draw/validation";
import { HistoryContext, useHistory } from "fond/history";
import { OverlayBlendingLayer, UploadDesignOverlay } from "fond/layout/widgets/Map.styles";
import LayerSelection from "fond/map/LayerSelection/LayerSelection";
import MapOverlay from "fond/map/MapOverlay";
import { MapContext } from "fond/map/MapProvider";
import { EditMode, setEditMode } from "fond/project/redux";
import { useAppDispatch } from "fond/utils/hooks";
import { BlockSpinner, ConfirmModal } from "fond/widgets";

import DrawHandler from "../DrawHandler";

import { defaultOverrides } from "./styles";
import { useMultiLayerDrawingStyles } from "./useMultiLayerDrawingStyles";
import { useMultiLayerEdit } from "./useMultiLayerEdit";
import { useMultiLayerFeatures } from "./useMultiLayerFeatures";

const { constants: Constants } = MapboxDraw;

/**
 * This component is responsible for handling the editing of multiple layers simultaneously.
 * It retrieves the drawing styles for each specified layer and can be used to render an editing UI.
 *
 * Must be used within a MultiLayerEditProvider.
 *
 * If initialFeatures is not provided in the context, it will automatically fetch features for all layers
 * specified in options.allLayers.
 */
const MultiLayerEditHandler: React.FC = () => {
  const dispatch = useAppDispatch();
  const { map, drawControl, setIsDirty } = useContext(MapContext);
  const { versionId, options, config, isSaving, initialFeatures, setResetCallback, setGetFeaturesCallback, setLayerStyleIds } = useMultiLayerEdit();
  const validatorRef = useRef<{
    validator: GenericValidator;
    validationFunctions: Map<string, ValidationFunction>;
  } | null>(null);

  const { clear: clearHistory } = useHistory<FeatureCollection>(HistoryContext);
  const [resetConfirmationOpen, setResetConfirmationOpen] = useState(false);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  // Track the editing layer to detect changes
  const previousLayerId = useRef<string | undefined>(drawControl.current?.options?.editOptions?.editingLayerId);
  // Track the mode key to detect if modes actually changed
  const previousModeKey = useRef<string | undefined>();

  // Define the layer keys that we want to edit simultaneously
  const layerKeys = useMemo(() => options?.editOptions?.allLayers?.map((layer) => layer.ID) || [], [options]);

  // Fetch features only if initialFeatures not provided and not currently saving
  // (skip fetching during save to prevent overwriting features being saved)
  const shouldFetch = !initialFeatures && !isSaving;
  const { features: fetchedFeatures, isLoading } = useMultiLayerFeatures(versionId, shouldFetch ? layerKeys : []);

  // Use provided features or fetched features (extract features from FeatureCollection if provided)
  const rawFeatures = initialFeatures?.features || fetchedFeatures;

  // Validate features before passing to DrawHandler
  const finalFeatures = useMemo(() => {
    if (!rawFeatures || rawFeatures.length === 0) return rawFeatures;
    if (!options?.editOptions?.allLayers) return rawFeatures;

    // Build validation functions map
    const validationFunctions = new Map<string, ValidationFunction>();
    options.editOptions.allLayers.forEach((layer) => {
      if (layer.validate) {
        validationFunctions.set(layer.ID, layer.validate);
      }
    });

    if (validationFunctions.size === 0) return rawFeatures;

    // Create validator with all features
    const validator = new GenericValidator(rawFeatures);

    // Validate each feature and set properties directly
    return rawFeatures.map((feature) => {
      const layerId = feature.properties?.layerId;
      if (!layerId || !validationFunctions.has(layerId)) return feature;

      const validationFn = validationFunctions.get(layerId)!;
      const result = validator.validate(feature, validationFn);

      // Set validation properties directly on the feature
      return {
        ...feature,
        properties: {
          ...feature.properties,
          [DRAWING_CONSTANTS.Valid]: result.valid.toString(),
          [DRAWING_CONSTANTS.ValidationErrors]: result.valid ? "" : result.errors.join("; "),
        },
      };
    });
  }, [rawFeatures, options?.editOptions?.allLayers]);

  const sources: FeatureIdentifier[] = useMemo(
    () =>
      layerKeys.map((id) => ({
        source: "project-layers",
        sourceLayer: id,
      })),
    [layerKeys]
  );

  // Get the drawing styles for all layers at once
  const layerStylesMap = useMultiLayerDrawingStyles(versionId, layerKeys);

  // Extract style IDs for each layer and update context
  // Track previous style IDs to prevent infinite updates
  const prevStyleIdsRef = useRef<Record<string, string[]> | undefined>(undefined);

  useEffect(() => {
    const styleIds: Record<string, string[]> = {};
    layerStylesMap.forEach((styles, layerId) => {
      if (styles) {
        styleIds[layerId] = styles.map((s) => s.id);
      }
    });

    // Only update if the style IDs actually changed (deep comparison)
    if (!isEqual(prevStyleIdsRef.current, styleIds)) {
      prevStyleIdsRef.current = styleIds;
      setLayerStyleIds(styleIds);
    }
  }, [layerStylesMap, setLayerStyleIds]);

  // Combine all layer styles with filtered default styles
  const styles = useMemo(() => {
    // Filter out default styles that we're replacing with layer-specific styles
    const filtered = defaultStyles.filter(
      ({ id }) =>
        ![
          "gl-draw-line-inactive",
          "gl-draw-point-inactive",
          "gl-draw-point-active",
          "gl-draw-point-point-stroke-inactive",
          "gl-draw-point-point-stroke-active",
          "gl-draw-polygon-inactive",
          "gl-draw-polygon-active",
          "gl-draw-polygon-stroke-inactive",
          "gl-draw-point-stroke-active",
        ].includes(id)
    );

    // Collect all styles from all layers (including per-layer validation styles)
    const allLayerStyles = Array.from(layerStylesMap.values())
      .filter((layerStyles): layerStyles is NonNullable<typeof layerStyles> => layerStyles !== undefined)
      .flat();

    // Combine: layer styles (including validation), then defaults
    // Layer styles are reversed so validation styles render on top
    const combined = [...allLayerStyles.reverse(), ...filtered, ...defaultOverrides];
    return combined;
  }, [layerStylesMap]);

  // Internal function that performs the actual reset
  const performReset = useCallback(() => {
    if (!drawControl.current || !finalFeatures) return;

    // Remove all current features
    drawControl.current.deleteAll();

    // Add back final features
    if (finalFeatures.length > 0) {
      drawControl.current.add({
        type: "FeatureCollection",
        features: finalFeatures,
      });
    }
    clearHistory();
    setResetConfirmationOpen(false);
    setIsDirty(false);
  }, [drawControl, finalFeatures, clearHistory, setIsDirty]);

  // Register callbacks with context so parent components can access these functions
  useEffect(() => {
    // Register reset callback
    setResetCallback(() => {
      setResetConfirmationOpen(true);
    });

    // Register getFeatures callback
    setGetFeaturesCallback(() => {
      if (!drawControl.current) return [];
      return drawControl.current.getAll().features;
    });
  }, [setResetCallback, setGetFeaturesCallback, drawControl]);

  // Handle validation on feature creation
  const handleValidateCreatedFeature = useCallback(
    (event: MapboxDraw.DrawCreateEvent) => {
      if (!drawControl.current) return;

      // Get the created feature IDs from the event
      const createdFeatureIds = event.features.map((f) => f.id as string);

      // Use the validation helper to validate created features
      validateCreatedFeatures(drawControl.current, createdFeatureIds, options?.editOptions?.allLayers, validatorRef, map);
    },
    [drawControl, options?.editOptions?.allLayers, map]
  );

  // Listen for draw.create events and validate after properties are set
  useEffect(() => {
    if (!map) return;

    // Use setTimeout to ensure validation runs AFTER DrawHandler's handleOnCreate sets the properties
    const handleCreate = (event: MapboxDraw.DrawCreateEvent) => {
      setTimeout(() => {
        handleValidateCreatedFeature(event);
      }, 0);
    };

    map.on(Constants.events.CREATE, handleCreate);

    // eslint-disable-next-line consistent-return
    return () => {
      map.off(Constants.events.CREATE, handleCreate);
    };
  }, [map, handleValidateCreatedFeature]);

  // Handle layer selection changes from LayerSelection component
  const handleLayerChange = useCallback(
    (event: {
      layerId: string;
      parentLayerId?: string;
      sublayerFilter?: mapboxgl.ExpressionSpecification;
      editingLayer?: { ID: string; options?: Record<string, unknown> };
    }) => {
      if (!drawControl.current?.options || !map) return;

      const { layerId, parentLayerId, sublayerFilter, editingLayer } = event;

      // Preserve colocation setting, editing layers, and sublayer metadata from current options
      const { topologicalEditing } = drawControl.current.options;
      const currentEditingLayerIds = drawControl.current.options.editOptions?.editingLayerIds;
      const currentEditableSublayers = drawControl.current.options.editOptions?.editableSublayers;

      // Merge base options with layer-specific options
      // @ts-expect-error - Custom property added in DrawHandler
      const baseOptions = drawControl.current.baseOptions || {};
      const mergedOptions = {
        ...baseOptions,
        ...(editingLayer?.options || {}),
        // Set these AFTER layer options to ensure they're not overridden
        editOptions: {
          ...baseOptions.editOptions,
          ...(editingLayer?.options?.editOptions || {}),
          editingLayerId: layerId,
          allLayers: drawControl.current.options.editOptions?.allLayers,
          editingLayerIds: currentEditingLayerIds, // Preserve editing layers
          editableSublayers: currentEditableSublayers, // Preserve sublayer metadata
        },
        // Preserve colocation setting
        topologicalEditing,
        // Add sublayer filter if present
        sublayerFilter: sublayerFilter || undefined,
        parentLayerId: parentLayerId || undefined,
      };

      // Delete properties that were in previous options but aren't in merged options
      // This ensures layer-specific overrides are properly removed when switching layers
      const currentOptions = drawControl.current.options;
      Object.keys(currentOptions).forEach((key) => {
        if (!(key in mergedOptions) && key !== "baseOptions") {
          delete currentOptions[key as keyof typeof currentOptions];
        }
      });

      // Mutate existing options object instead of replacing it
      // This ensures the reference stays the same for modes that cache it
      Object.assign(currentOptions, mergedOptions);

      // Deselect any selected features when changing layers
      drawControl.current.changeMode("simple_select");
      map.fire("draw.modechange", {
        type: "draw.modechange",
        mode: "simple_select",
      });
    },
    [map, drawControl]
  );

  useEffect(() => {
    if (!map) return;

    map.on("draw.layerchange", handleLayerChange);

    // eslint-disable-next-line consistent-return
    return () => {
      map.off("draw.layerchange", handleLayerChange);
    };
  }, [map, handleLayerChange]);

  // Reinitialize the draw control when the editing layer changes and modes are different
  useEffect(() => {
    if (!map) return;
    if (!drawControl.current) return;

    const currentLayerId = drawControl.current.options?.editOptions?.editingLayerId;
    // Look up the full layer object from allLayers
    const currentLayer = drawControl.current.options?.editOptions?.allLayers?.find((layer) => layer.ID === currentLayerId);

    // Get the mode key to determine if modes changed
    const currentModeKey = currentLayer?.ModeKey;

    // Check if the editing layer has changed AND modes are different
    const layerChanged = currentLayerId && currentLayerId !== previousLayerId.current;
    const modesChanged = currentModeKey !== previousModeKey.current;

    if (layerChanged && modesChanged) {
      // Determine which modes to use
      const modesToUse = currentLayer?.Modes ? { ...baseDrawControlModes, ...currentLayer.Modes } : baseDrawControlModes;
      // Preserve existing features
      const existingFeatures = drawControl.current.getAll();

      // Preserve current options
      const currentOptions = { ...drawControl.current.options };

      // Get the current mode before removing the control
      const currentMode = drawControl.current.getMode();

      // Remove the old control
      if (map.hasControl(drawControl.current)) {
        drawControl.current.changeMode("simple_select");
        map.removeControl(drawControl.current);
      }

      // Create new draw control with updated modes
      drawControl.current = new MapboxDraw({
        displayControlsDefault: false,
        ...currentOptions,
        modes: modesToUse,
        styles: currentOptions.styles || defaultStyles,
      });

      // Add the new control to the map
      map.addControl(drawControl.current);

      // Restore the features
      if (existingFeatures.features.length > 0) {
        drawControl.current.set(existingFeatures);
      }

      // Restore the previous mode
      try {
        drawControl.current.changeMode(currentMode as string);
      } catch {
        // If the mode doesn't exist in the new control, default to simple_select
        drawControl.current.changeMode("simple_select");
      }

      // Update the tracked layer ID and mode key
      previousLayerId.current = currentLayerId;
      previousModeKey.current = currentModeKey;
    }
  }, [drawControl, map, drawControl.current?.options?.editOptions?.editingLayerId]);

  // Enhance options with default snapGetFeatures if not provided
  const enhancedOptions = useMemo(() => {
    // If snapGetFeatures is not defined, provide a default that filters by visible layers
    if (!options?.snapOptions?.snapGetFeatures) {
      return {
        ...options,
        snapOptions: {
          ...options?.snapOptions,
          snapGetFeatures: () => {
            if (!drawControl.current) return [];

            const allFeatures = drawControl.current.getAll().features;
            const visibleLayerIds = drawControl.current.options?.visibleLayerIds;

            // If no visibility filter is set, return all features
            if (!visibleLayerIds || visibleLayerIds.length === 0) {
              return allFeatures;
            }

            // Filter features to only include those from visible layers
            return allFeatures.filter((feature) => {
              const layerId = feature.properties?.layerId;
              return layerId && visibleLayerIds.includes(layerId);
            });
          },
        },
      };
    }

    return options;
  }, [options, drawControl]);

  // Show loading spinner while fetching features
  if (shouldFetch && isLoading) {
    return <BlockSpinner />;
  }

  return (
    <>
      {resetConfirmationOpen && (
        <ConfirmModal
          open
          header="Reset Changes"
          content="Are you sure you want to reset all changes? This will revert all features back to their initial state."
          confirmText="Reset"
          cancelText="Cancel"
          onConfirm={performReset}
          onCancel={() => setResetConfirmationOpen(false)}
          color="error"
        />
      )}
      {!isSaving && drawControl.current && (
        <MapOverlay showOverlay={false} centerContent={false}>
          <LayerSelection />
        </MapOverlay>
      )}
      {isSaving && (
        <MapOverlay>
          <>
            <OverlayBlendingLayer />
            <UploadDesignOverlay>
              <BlockSpinner />
              <Typography variant="h5" fontWeight={700}>
                Saving your changes!
              </Typography>
              <Typography variant="h6" fontWeight={400}>
                This won't take long...
              </Typography>
            </UploadDesignOverlay>
          </>
        </MapOverlay>
      )}
      <DrawHandler disableHoverEffects config={config} options={enhancedOptions} initialFeatures={finalFeatures} sources={sources} styles={styles} />
    </>
  );
};

MultiLayerEditHandler.displayName = "MultiLayerEditHandler";

export default MultiLayerEditHandler;
